import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import * as Sentry from '@sentry/nextjs'
import { PAGINATION_ORDER } from 'shared/constants'
import { usePinnedComments } from 'modules/comments/api/get-pinned-comments'
import { Comment } from 'modules/comments/components/comment'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import {
  handleTargetComment,
  parseParentIdFromKey,
} from 'modules/comments/components/comments-list/utils'
import { useCommentHighlight } from 'modules/comments/contexts/comment-highlight'
import type { OnErrorType } from 'modules/comments/types'
import { mergeComments } from 'modules/comments/utils/merge-comments'
import { useCommentsContext } from './index'

export interface PinnedCommentsListProps {
  postId: number
  targetCommentId?: number

  // Props for comment component
  setActiveComment: (comment: CommentInterface | null) => void
  activeComment: CommentInterface | null
  onError?: OnErrorType
}

export const PinnedCommentsList = ({
  postId,
  setActiveComment,
  activeComment,
  onError,
  targetCommentId,
}: PinnedCommentsListProps): ReactNode => {
  const { pinnedComments, setPinnedComments, updateReplies, repliesList } = useCommentsContext()
  const { setHighlightedComment, highlightedComment } = useCommentHighlight()
  const [initialTargetCommentId, setInitialTargetCommentId] = useState<number | undefined>(
    targetCommentId,
  )
  const [requestParams, setRequestParams] = useState<{
    lastCommentId: number
    parentId: number | null
  }>({ lastCommentId: 0, parentId: null })

  // Pinned posts are only displayed in the community
  usePinnedComments({
    parentId: requestParams.parentId,
    lastCommentId: requestParams.lastCommentId,
    targetCommentId:
      requestParams.lastCommentId === 0 && !requestParams.parentId && !!initialTargetCommentId
        ? initialTargetCommentId
        : undefined,
    postId,
    sortOrder: PAGINATION_ORDER.ASC,
    config: {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: data => {
        setPinnedComments(prev => mergeComments(prev, data ?? []))
        data.forEach(comment => {
          if (comment.children) {
            updateReplies({
              commentId: comment.id,
              replies: mergeComments(
                [...(repliesList[comment.id]?.replies ?? [])],
                comment.children,
              ),
            })
          }
        })

        handleTargetComment(
          data,
          initialTargetCommentId,
          setHighlightedComment,
          highlightedComment,
          setInitialTargetCommentId,
        )
      },
      onError: (error, key) => {
        const { isReply, parentId } = parseParentIdFromKey(key)

        if (isReply) {
          updateReplies({ commentId: parentId, replies: [] })
        }

        Sentry.captureException(error, {
          extra: { context: 'fetch pinned comments error' },
        })
      },
    },
  })

  const handleLoadMoreGap = useCallback(
    ({ lastCommentId, parentId }: { lastCommentId: number; parentId: number | null }) => {
      if (parentId) {
        updateReplies({ commentId: parentId })
        setRequestParams({ lastCommentId, parentId })
      } else {
        setRequestParams({ lastCommentId, parentId: null })
      }
    },
    [updateReplies],
  )

  return (
    <>
      {pinnedComments.map(pinnedComment => (
        <Comment
          key={pinnedComment.id}
          data={pinnedComment}
          level={0}
          setActiveComment={setActiveComment}
          activeComment={activeComment}
          onError={onError}
          replies={repliesList[pinnedComment.id]?.replies}
          loadMoreReplies={handleLoadMoreGap}
        />
      ))}
    </>
  )
}
